import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Mail, Lock, Eye, EyeOff, LogIn, AlertCircle, Loader2 } from 'lucide-react'
import { supabase } from '@/config/supabase'
import { authService, LoginCredentials } from '@/services/auth'
import { analytics, trackEvent } from '@/services/analytics'

export default function LoginPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: ''
  })
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isIOS, setIsIOS] = useState(false)
  const [gsiLoaded, setGsiLoaded] = useState(false)
  const googleClientId = (import.meta as any).env?.VITE_GOOGLE_CLIENT_ID

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error when user starts typing
    if (error) setError(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    // PostHog: login butonu/funneli icin event (submit denemesi)
    trackEvent({
      event: 'scraible_login_submit',
      properties: {
        method: 'email_password',
        page: 'login',
      },
    })

    setIsLoading(true)
    setError(null)

    try {
      const user = await authService.signIn(formData)

      // PostHog kimligi olarak Supabase user.id kullan
      analytics.setDistinctId(user.id)
      trackEvent({
        event: 'scraible_login_success',
        distinct_id: user.id,
        properties: {
          method: 'email_password',
          page: 'login',
          email: user.email,
        },
      })

      try { sessionStorage.setItem('welcome_message', 'Hoş geldin!') } catch {}
      navigate('/')
    } catch (err) {
      // Login hatalarini da topla
      trackEvent({
        event: 'scraible_login_error',
        properties: {
          method: 'email_password',
          page: 'login',
        },
      })
      setError(err instanceof Error ? err.message : 'Giriş yapılırken bir hata oluştu')
    } finally {
      setIsLoading(false)
    }
  }

  // Check if running on iOS
  useEffect(() => {
    const checkPlatform = async () => {
      try {
        const { Capacitor } = await import('@capacitor/core')
        setIsIOS(Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios')
      } catch (error) {
        // If Capacitor is not available, check user agent
        setIsIOS(/iPad|iPhone|iPod/.test(navigator.userAgent))
      }
    }
    checkPlatform()
  }, [])

  // Google pre-built callback (global)
  React.useEffect(() => {
    ;(window as any).handleSignInWithGoogle = async (response: any) => {
      try {
        const { error } = await supabase.auth.signInWithIdToken({
          provider: 'google',
          token: response?.credential,
        })
        if (error) throw error
        try { sessionStorage.setItem('welcome_message', 'Hoş geldin!') } catch {}
        navigate('/')
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Google ile giriş yapılırken hata oluştu')
      }
    }
    return () => {
      try { delete (window as any).handleSignInWithGoogle } catch {}
    }
  }, [navigate])

  // Initialize Google pre-built button (SPA güvenli)
  React.useEffect(() => {
    if (!googleClientId) return

    const init = () => {
      const google = (window as any).google
      console.log('Google available:', !!google, 'Accounts API:', !!google?.accounts?.id)
      
      if (google?.accounts?.id) {
        console.log('Initializing Google Sign-In for platform:', isIOS ? 'iOS' : 'other')
        google.accounts.id.initialize({
          client_id: googleClientId,
          callback: (window as any).handleSignInWithGoogle,
          ux_mode: isIOS ? 'redirect' : 'popup',
          use_fedcm_for_prompt: true,
        })
        const container = document.getElementById('googleSignInButton')
        console.log('Container found:', !!container, 'Children count:', container?.childElementCount)
        
        if (container && container.childElementCount === 0) {
          console.log('Rendering Google button')
          google.accounts.id.renderButton(container, {
            theme: 'filled_black',
            size: 'large',
            shape: 'pill',
            text: 'signin_with',
            logo_alignment: 'left',
            type: 'standard',
          })
          setGsiLoaded(true)
        }
      } else {
        console.log('Google Accounts API not available - will show fallback')
        setGsiLoaded(false)
      }
    }

    // Hemen dene, yoksa script yüklenmesini bekle
    init()
    const interval = setInterval(() => {
      const google = (window as any).google
      if (google?.accounts?.id) {
        init()
        clearInterval(interval)
      }
    }, 300)
    
    // 5 saniye sonra GSI yüklenmemişse fallback göster
    const fallbackTimeout = setTimeout(() => {
      if (!gsiLoaded) {
        console.log('GSI timeout - showing fallback')
        setGsiLoaded(false)
      }
    }, 5000)
    
    return () => {
      clearInterval(interval)
      clearTimeout(fallbackTimeout)
    }
  }, [googleClientId, isIOS, gsiLoaded])

  

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-md">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gradient mb-2">
            {t('auth.welcome')}
          </h1>
          <p className="text-white/60 text-lg">
            {t('auth.welcomeSubtitle')}
          </p>
        </motion.div>

        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card p-6 md:p-8"
        >
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Error Message */}
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex items-center space-x-3 p-4 bg-red-500/20 border border-red-500/30 rounded-xl text-red-300"
              >
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </motion.div>
            )}

            {/* Email Field */}
            <div className="space-y-2">
              <label className="block text-white/80 text-sm font-medium">
                {t('auth.email')}
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="input-field pl-11"
                  placeholder={t('auth.emailPlaceholder') as string}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            {/* Password Field */}
            <div className="space-y-2">
              <label className="block text-white/80 text-sm font-medium">
                {t('auth.password')}
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="input-field pl-11 pr-11"
                  placeholder="••••••••"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/40 hover:text-white/60 transition-colors"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn-primary w-full flex items-center justify-center space-x-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('auth.signingIn')}</span>
                </>
              ) : (
                <>
                  <LogIn className="w-5 h-5" />
                  <span>{t('auth.signIn')}</span>
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center my-6">
            <div className="flex-1 border-t border-white/10"></div>
            <span className="px-4 text-white/40 text-sm">{t('common.or')}</span>
            <div className="flex-1 border-t border-white/10"></div>
          </div>

          {/* Forgot Password */}
          <div className="text-center mb-6">
            <Link
              to="/forgot-password"
              className="text-white/60 hover:text-white/80 text-sm"
            >
              {t('auth.forgotPassword')}
            </Link>
          </div>

          

          {/* Social Login Buttons */}
          <div className="space-y-3 mt-4">
            {/* Google Login - Official GSI button works on all platforms */}
            {googleClientId ? (
              <>
                <div id="googleSignInButton" />
                {/* Fallback button if GSI doesn't load on native */}
                {!gsiLoaded && (
                  <button
                    type="button"
                    onClick={async () => {
                      setIsLoading(true)
                      try {
                        // Native'de idToken akışı, web'de redirect akışı kullanılır
                        await authService.signInWithGoogle('/auth/callback')
                        try { sessionStorage.setItem('welcome_message', 'Hoş geldin!') } catch {}
                        navigate('/')
                      } catch (err) {
                        setError(err instanceof Error ? err.message : 'Google ile giriş yapılırken hata oluştu')
                      } finally {
                        setIsLoading(false)
                      }
                    }}
                    disabled={isLoading}
                    className="btn-secondary w-full flex items-center justify-center space-x-3"
                  >
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                    <span>{t('auth.googleSignIn')}</span>
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                onClick={() => setError('Google Client ID bulunamadı. .env dosyanızı kontrol edin.')}
                disabled={isLoading}
                className="btn-secondary w-full flex items-center justify-center space-x-3 opacity-50 cursor-not-allowed"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                <span>Google Client ID Eksik</span>
              </button>
            )}

            {/* Apple Sign-In Button - Only show on iOS */}
            {isIOS && (
              <button
                type="button"
                onClick={async () => {
                  setIsLoading(true)
                  try {
                    await authService.signInWithApple()
                    try { sessionStorage.setItem('welcome_message', 'Hoş geldin!') } catch {}
                    navigate('/')
                  } catch (err) {
                    setError(err instanceof Error ? err.message : 'Apple ile giriş yapılırken hata oluştu')
                  } finally {
                    setIsLoading(false)
                  }
                }}
                disabled={isLoading}
                className="btn-secondary w-full flex items-center justify-center space-x-3 bg-black hover:bg-gray-900 border-gray-700"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                <span>{t('auth.appleSignIn')}</span>
              </button>
            )}
          </div>

          {/* Register Link */}
          <div className="text-center mt-6">
            <p className="text-white/60 text-sm">
              {t('auth.noAccount')} {' '}
              <Link
                to="/register"
                className="text-gradient hover:text-gradient-gold transition-colors font-medium"
              >
                {t('auth.register')}
              </Link>
            </p>
          </div>
        </motion.div>

        {/* Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-8"
        >
          <Link
            to="/"
            className="text-white/40 hover:text-white/60 transition-colors text-sm"
          >
            ← {t('auth.backHome')}
          </Link>
        </motion.div>
      </div>
    </div>
  )
}
